<template>
  <!-- add class 'selected' for selected state -->
  <card-component :class="cssClass" size="large" :truncate="true" @on-click="handleOnClick">
    <template v-if="isLastUsed" #label><span>Last Used</span></template>
    <template #thumbnail>
      <svg-icon :name="icon" :kind="iconType" />
    </template>
    <template #header>
      <h5>{{ title }}</h5>
    </template>
  </card-component>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useStore } from "vuex";
import CardComponent from "@plugins/card/components/CardComponent.vue";
import SvgIcon from "@plugins/common/components/SvgIcon.vue";
import { appLocalStorage } from "@utils/app.storage";
import { NAMESPACE } from "@/modules/auth/service/login.constant";
const DA_LAST_USED_RECIPE_KEY = "lastUsedRecipe";

interface Props {
  id?: number;
  title?: string;
  content?: string;
  isSelected?: boolean;
  icon?: string;
  iconType?: string;
  kind?: string;
}

const {
  id = 0,
  title = "",
  content = "",
  isSelected = false,
  icon = "Recipes32",
  iconType = "indicative",
  kind = "",
} = defineProps<Props>();

type EmitFn = (e: "onClick", payload: { id?: number; content: string }) => void;

const emit = defineEmits<EmitFn>();

const store = useStore();

const validateKind = (value: string): boolean => {
  return ["", "gray"].includes(value);
};

const userId = computed<string | number | undefined>(
  () => store.getters[`${NAMESPACE.AUTH}/userId`],
);

const isLastUsed = computed<boolean>(() => {
  const key = `${DA_LAST_USED_RECIPE_KEY}-${userId.value}`;
  const raw = appLocalStorage.getItem(key) ?? "";
  let parsed: unknown;
  try {
    parsed = raw ? JSON.parse(raw) : undefined;
  } catch {
    parsed = undefined;
  }
  return id === (parsed as number | undefined);
});

const cssClass = computed<string>(() => {
  let classes = "recipe-card";

  if (kind !== "" && validateKind(kind)) {
    classes += ` recipe-card-${kind}`;
  }

  if (isSelected) {
    classes += " selected";
  }

  return classes;
});

function handleOnClick() {
  emit("onClick", { id: id, content: content });
}
</script>

<style lang="scss">
@use "@styles/abstracts/mixins" as *;
@use "@styles/layout/breakpoint" as *;
@use "@styles/layout/z-map" as *;
@use "@styles/themes/sls-theme/tokens/colors" as *;
@use "@styles/themes/sls-theme/tokens/effects" as *;
@use "@styles/themes/sls-theme/tokens/spacing" as *;
@use "@styles/themes/sls-theme/tokens/typo" as *;

.card-component.recipe-card {
  position: relative;
  flex-flow: row nowrap;
  align-items: center;
  gap: $spacing-05;
  height: 100%;
  min-height: 108px;
  background-color: $ui-01;
  padding: $spacing-06 $spacing-05;
  border-color: $ui-04;
  overflow: unset;

  &:has(.card-label) {
    &::before {
      position: absolute;
      content: "";
      background-color: $poll-06;
      height: 20px;
      width: 20px;
      top: 5px;
      left: -2px;
      transform: rotate(45deg);
      opacity: 0.5;
      z-index: calc(-#{z-map("default")});
    }
  }

  &:hover,
  &.selected {
    border-color: $ai;
    box-shadow: $shadow-02;
  }

  &.recipe-card-gray {
    background-color: $ui-02;
  }

  .card-label {
    position: absolute;
    top: -3px;
    left: -6px;
    background-color: $poll-06;
    border-radius: 4px 4px 4px 0px;
    box-shadow: 0px 2px 6px 0px rgba(0, 0, 0, 0.3);
  }

  .card-thumbnail {
    display: inline-flex;
  }

  .card-header {
    flex: 1;
    padding: 0;

    h5 {
      @include carbon--type-style("card-title-01");
      @include line-clamp(3);
    }
  }
}
</style>
